import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import { IntegrationServices } from '@sequencehq/api/utils/commonEnums'
import { FormFields, useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import { getIntegrationName } from 'Integrations/utils/getIntegrationName'
import { useNotifications } from '@sequencehq/core-components'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  dashboardv99990101Client,
  type Dashboardv99990101CommonModels
} from '@sequencehq/api/dashboard/v99990101'
import { storeEntityLink } from 'Integrations/integrationsConfig/common/shared'
import { SelectableCustomer } from 'Integrations/components/integrationCustomerPicker'
import { supportsCustomerSearch } from '../supportsCustomerSearch'
import { integrationCustomerName } from '../integrationCustomerName'
import { toCustomerUpdateBody } from 'modules/Customer'

interface LoadedData {
  customer: Dashboardv99990101CommonModels.Customers.Customer | null
  serviceAccounts:
    | DashboardApi20240730.GetIntegrationCustomersService.IntegrationCustomer[]
    | null
}

enum Status {
  UNINITIALIZED = 'UNINITIALIZED',
  LOADING = 'LOADING',
  LINKING = 'LINKING',
  LOADED = 'LOADED'
}

type FormData = {
  linkToAccount: string
}

type UseLinkCustomerModal = (props: {
  onConfirm?: () => void
  customerId: string
  service: IntegrationServices
}) => {
  fields: FormFields<FormData>
  customerName: string
  serviceName: string
  loaded: boolean
  /**
   * True when the connector is searched as the user types. The same answer
   * drives the loading, so the modal can't end up rendering a typeahead over a
   * list we already fetched (or the other way round).
   */
  searchesCustomers: boolean
  externalAccountDetails: {
    name: string
    externalUrl: string
  } | null
  select: {
    customers: SelectableCustomer[]
    unfilteredCustomerCount: number
    allCustomersLinked: boolean
  }
  submit: {
    disabled: boolean
    linking: boolean
    onClick: () => Promise<boolean>
  }
}

export const useLinkCustomerModal: UseLinkCustomerModal = props => {
  const [status, setStatus] = useState<Status>(Status.UNINITIALIZED)
  const { displayNotification } = useNotifications()
  const [loadedData, setLoadedData] = useState<LoadedData>({
    customer: null,
    serviceAccounts: null
  })

  /**
   * Grab the current data, for this service, from the customer object.
   */
  const selectedOption = useMemo(() => {
    return loadedData.customer?.integrationIds.find(
      int => int.service === props.service
    )
  }, [loadedData, props.service])

  const serviceName = useMemo(() => {
    return getIntegrationName(props.service)
  }, [props.service])

  const searchesCustomers = supportsCustomerSearch(props.service)

  /**
   * Form management
   */
  const selectableCustomers: SelectableCustomer[] = useMemo(
    () =>
      (loadedData.serviceAccounts ?? [])
        .filter(account => !account.isLinked)
        .map(account => ({
          id: account.id,
          name: integrationCustomerName(account),
          country: account.country,
          isDisabled: false,
          invalidReason: account.invalidReason,
          validationErrors: account.validationErrors,
          externalUrl: account.externalUrl,
          contacts: account.contacts
        })),
    [loadedData.serviceAccounts]
  )

  const form = useForm<FormData>({
    value: {
      linkToAccount: selectedOption?.id ?? ''
    },
    fieldConfiguration: [
      {
        property: 'linkToAccount',
        validation: [required]
      }
    ]
  })

  /**
   * API interactions
   */

  /**
   * A simple load function which will grab the customer and the integration
   * customers for the given service.
   */
  const load = useCallback(
    async ({
      customerId,
      service
    }: {
      customerId: string
      service: IntegrationServices
    }) => {
      setStatus(Status.LOADING)

      if (searchesCustomers) {
        /**
         * The typeahead asks the connector for the customers matching what the
         * user types, so there is nothing to fetch up front. Listing them all
         * would be very slow on an account with thousands of them.
         */
        const customer = await dashboardv99990101Client.getCustomer({
          id: customerId
        })

        setLoadedData({
          customer: customer.data ?? null,
          serviceAccounts: null
        })

        setStatus(Status.LOADED)
        return
      }

      const [customer, serviceAccounts] = await Promise.all([
        dashboardv99990101Client.getCustomer({ id: customerId }),
        dashboard20240730Client.getIntegrationCustomersService({ service })
      ])

      setLoadedData({
        customer: customer.data ?? null,
        serviceAccounts: serviceAccounts.data?.items ?? null
      })

      setStatus(Status.LOADED)
    },
    [searchesCustomers]
  )

  /**
   * Link the customer to an account on the third party service.
   */
  const linkAccount = useCallback(async (): Promise<boolean> => {
    if (status === Status.LINKING || !loadedData.customer) {
      return false
    }

    setStatus(Status.LINKING)
    const res = await dashboardv99990101Client.putCustomer({
      id: props.customerId,
      body: {
        ...toCustomerUpdateBody(loadedData.customer),
        integrationIds: storeEntityLink(
          loadedData.customer.integrationIds,
          props.service,
          form.fields.linkToAccount.value
        )
      }
    })

    if (res.error) {
      displayNotification(`Could not link customer to ${serviceName}`, {
        type: 'error'
      })
      setStatus(Status.LOADED)
      return false
    }

    displayNotification(`Customer linked to ${serviceName}`, {
      type: 'success'
    })
    setStatus(Status.LOADED)
    return true
  }, [
    form.fields.linkToAccount.value,
    loadedData,
    props.customerId,
    props.service,
    status,
    displayNotification,
    serviceName
  ])

  const externalAccountDetails = useMemo(() => {
    const externalAccount = loadedData.serviceAccounts?.find(
      account => account.id === form.fields.linkToAccount.value
    )

    if (!externalAccount) {
      return null
    }

    return {
      name: externalAccount.companyName ?? '',
      externalUrl: externalAccount.externalUrl ?? ''
    }
  }, [loadedData.serviceAccounts, form.fields.linkToAccount.value])

  const submit = useMemo(() => {
    return {
      disabled: !form.queries.isValid || status === Status.LINKING,
      linking: status === Status.LINKING,
      onClick: async (): Promise<boolean> => {
        const success = await linkAccount()
        if (success) {
          props.onConfirm?.()
        }
        return success
      }
    }
  }, [form.queries.isValid, linkAccount, props.onConfirm, status])

  /**
   * Side effects
   */
  useEffect(() => {
    void load({ customerId: props.customerId, service: props.service })
  }, [props.customerId, props.service, load])

  return {
    loaded: ![Status.UNINITIALIZED, Status.LOADING].includes(status),
    fields: form.fields,
    serviceName,
    searchesCustomers,
    externalAccountDetails,
    select: {
      customers: selectableCustomers,
      unfilteredCustomerCount: loadedData.serviceAccounts?.length ?? 0,
      allCustomersLinked:
        (loadedData.serviceAccounts?.length ?? 0) > 0 &&
        selectableCustomers.length === 0
    },
    customerName: loadedData.customer?.legalName ?? '',
    submit
  }
}
